/**
 * 设备指纹采集工具
 * 用于生成设备唯一标识，用于登录安全验证
 */

// 设备指纹信息接口
export interface DeviceInfo {
  fingerprint: string;
  screenResolution: string;
  timezone: string;
  language: string;
  platform: string;
  canvasFingerprint: string;
  webglFingerprint: string;
}

/**
 * 生成 Canvas 指纹
 */
function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    canvas.width = 200;
    canvas.height = 50;

    // 绘制文本
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('MinutesAI', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('fingerprint', 4, 17);

    // 获取数据并生成哈希
    const dataUrl = canvas.toDataURL();
    return hashString(dataUrl);
  } catch (e) {
    return '';
  }
}

/**
 * 生成 WebGL 指纹
 */
function getWebglFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';

    const webgl = gl as WebGLRenderingContext;
    const debugInfo = webgl.getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return '';

    const vendor = webgl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
    const renderer = webgl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

    return hashString(`${vendor}~${renderer}`);
  } catch (e) {
    return '';
  }
}

/**
 * 简单的字符串哈希函数
 */
function hashString(str: string): string {
  let hash = 0;
  if (str.length === 0) return hash.toString(16);

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return Math.abs(hash).toString(16);
}

/**
 * 生成综合指纹
 */
function generateFingerprint(components: string[]): string {
  const combined = components.join('|');
  return hashString(combined);
}

/**
 * 获取设备指纹信息
 * @returns 设备指纹信息对象
 */
export async function getDeviceFingerprint(): Promise<DeviceInfo> {
  const screenResolution = `${screen.width}x${screen.height}`;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || String(new Date().getTimezoneOffset());
  const language = navigator.language || '';
  const platform = navigator.platform || '';
  const canvasFingerprint = getCanvasFingerprint();
  const webglFingerprint = getWebglFingerprint();

  // 收集更多特征用于生成综合指纹
  const components = [
    navigator.userAgent,
    screenResolution,
    timezone,
    language,
    platform,
    canvasFingerprint,
    webglFingerprint,
    String(screen.colorDepth),
    String(navigator.hardwareConcurrency || ''),
    String(navigator.maxTouchPoints || 0),
  ];

  const fingerprint = generateFingerprint(components);

  return {
    fingerprint,
    screenResolution,
    timezone,
    language,
    platform,
    canvasFingerprint,
    webglFingerprint,
  };
}

/**
 * 获取简化的设备指纹（仅返回指纹字符串）
 */
export async function getSimpleFingerprint(): Promise<string> {
  const info = await getDeviceFingerprint();
  return info.fingerprint;
}
